/**
 * IELTS V2 Resource Hub — /resources/ielts
 * Academic/GT selector · Listening & Reading practice · instant marking
 * Reuses shared Navigation, Footer, and cookie-authenticated axios.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, PlayCircle, Headphones, BookOpen, PenSquare, Mic, CheckCircle2, XCircle, ArrowRight, Zap, History } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import { useNavigate, useSearchParams } from 'react-router-dom';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ax = axios.create({ baseURL: BACKEND, withCredentials: true });

export default function IeltsV2Page() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [variant, setVariant] = useState(() => localStorage.getItem('ielts_variant') || 'academic');
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const q = searchParams.get('skill');
    return ['listening', 'reading', 'writing', 'speaking'].includes(q) ? q : 'listening';
  });
  const [listening, setListening] = useState([]);
  const [reading, setReading] = useState([]);
  const [active, setActive] = useState(null);   // {kind, set}
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simHistory, setSimHistory] = useState([]);
  const nav = useNavigate();

  useEffect(() => { localStorage.setItem('ielts_variant', variant); }, [variant]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, r, h] = await Promise.all([
        ax.get(`/api/ielts/v2/listening-sets?variant=${variant}`),
        ax.get(`/api/ielts/v2/reading-sets?variant=${variant}`),
        ax.get('/api/ielts/v2/listening/simulation/history').catch(() => ({ data: { items: [] } })),
      ]);
      setListening(l.data?.items || []);
      setReading(r.data?.items || []);
      setSimHistory(h.data?.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [variant]);
  useEffect(() => { load(); }, [load]);

  const openSet = async (kind, id) => {
    setActive({ kind, set: null }); setAttempt(null);
    const r = await ax.get(`/api/ielts/v2/${kind}-sets/${id}`);
    setActive({ kind, set: r.data });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-28 md:pt-32 pb-16">
        <div className="text-xs uppercase tracking-widest text-indigo-500 mb-2 font-bold">Winhouse Tutors · IELTS Resource Centre</div>
        <h1 className="text-3xl md:text-5xl font-black text-[#172033]">IELTS <span className="text-indigo-500">Preparation</span></h1>
        <p className="text-slate-600 mt-2 max-w-2xl text-sm">Practise every IELTS skill under real conditions — Listening, Reading, Writing and Speaking — with instant marking, AI-graded feedback and Winhouse Estimated IELTS Practice Bands. Original Winhouse content — not affiliated with IDP, British Council or Cambridge Assessment English.</p>

        {/* Full Mock CTA — highest-priority pathway */}
        <div className="mt-6 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          data-testid="ielts-full-mock-banner">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Full Simulation</div>
            <div className="text-lg md:text-xl font-bold mt-0.5">IELTS Full Mock Exam — Listening · Reading · Writing · Speaking</div>
            <div className="text-xs opacity-90 mt-1 max-w-2xl">3 Academic + 3 General Training variants. Answers, transcripts and feedback stay locked until you submit all four sections — then receive a full Winhouse Estimated IELTS Practice Band report.</div>
          </div>
          <button onClick={() => nav('/resources/ielts/mock')}
            data-testid="ielts-full-mock-cta"
            className="inline-flex items-center gap-1 rounded-full bg-white text-indigo-700 font-bold text-sm px-5 py-2 hover:bg-slate-100 flex-none">
            Take Full Mock →
          </button>
        </div>

        {/* Variant selector */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="ielts-variant-tabs">
          {[['academic','IELTS Academic'],['general_training','IELTS General Training']].map(([k, l]) => (
            <button key={k} onClick={() => { setVariant(k); setActive(null); setAttempt(null); }}
              data-testid={`ielts-variant-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-bold border ${
                variant === k ? 'bg-[#172033] text-white border-[#172033]' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
              {l}
            </button>
          ))}
        </div>

        {/* Skill tabs */}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="ielts-skill-tabs">
          {[['listening', <Headphones className="h-4 w-4" />, 'Listening'],
             ['reading',   <BookOpen  className="h-4 w-4" />, 'Reading'],
             ['writing',   <PenSquare className="h-4 w-4" />, 'Writing'],
             ['speaking',  <Mic       className="h-4 w-4" />, 'Speaking']].map(([k, icon, l]) => (
            <button key={k} onClick={() => {
                if (k === 'writing') { nav('/resources/ielts/writing'); return; }
                if (k === 'speaking') { nav('/resources/ielts/speaking'); return; }
                setTab(k); setActive(null); setAttempt(null);
              }}
              data-testid={`ielts-tab-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 border ${
                tab === k ? 'bg-indigo-500 text-white border-indigo-500' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
              {icon} {l}
            </button>
          ))}
        </div>

        {/* Full Simulation CTA */}
        {tab === 'listening' && !active && !attempt && (
          <SimulationBanner history={simHistory} onStart={() => nav('/resources/ielts/listening/simulation')}
            onOpenResult={(id) => nav(`/resources/ielts/results/${id}`)} />
        )}
        {tab === 'reading' && !active && !attempt && (
          <ReadingSimulationBanner variant={variant}
            onStart={() => nav(`/resources/ielts/reading/simulation?variant=${variant}`)} />
        )}

        {/* List / Practice / Results */}
        <div className="mt-6">
          {loading ? (
            <div className="p-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
          ) : attempt ? (
            <ResultsView set={active?.set} kind={active?.kind} attempt={attempt}
              onRetry={() => { setAttempt(null); }}
              onBack={() => { setAttempt(null); setActive(null); load(); }} />
          ) : active?.set ? (
            <PracticeView kind={active.kind} set={active.set}
              onSubmitResult={(r) => setAttempt(r)}
              onBack={() => setActive(null)} />
          ) : (
            <SetList items={tab === 'listening' ? listening : reading} kind={tab}
                        onOpen={(id) => openSet(tab, id)} />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function SetList({ items, kind, onOpen }) {
  if (!items.length) return (
    <div className="p-6 rounded-2xl bg-white border border-slate-200 text-center text-sm text-slate-500 italic">
      No {kind} sets available for this variant yet.
    </div>
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`ielts-${kind}-list`}>
      {items.map((s) => (
        <button key={s.id} onClick={() => onOpen(s.id)}
          data-testid={`ielts-open-${s.id}`}
          className="text-left rounded-2xl bg-white border border-slate-200 hover:border-indigo-500 hover:shadow p-4 transition">
          <div className="text-xs uppercase tracking-widest text-indigo-500 font-bold">{kind === 'listening' ? 'Listening set' : 'Reading set'}</div>
          <div className="text-lg font-bold text-[#172033] mt-1">{s.title}</div>
          <div className="text-xs text-slate-500 mt-1">{s.question_count} questions · {s.difficulty || 'mixed'}</div>
          <div className="text-xs text-indigo-500 mt-3 inline-flex items-center gap-1"><PlayCircle className="h-3.5 w-3.5" /> Start practice</div>
        </button>
      ))}
    </div>
  );
}

function ReadingSimulationBanner({ variant, onStart }) {
  const label = variant === 'academic' ? 'IELTS Academic' : 'IELTS General Training';
  return (
    <div className="mt-4 rounded-2xl bg-gradient-to-br from-fuchsia-600 via-indigo-500 to-blue-500 text-white p-5 md:p-6 shadow-lg"
       data-testid="ielts-reading-sim-banner">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[11px] uppercase tracking-widest text-fuchsia-100 font-bold inline-flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5" /> Full Simulation
          </div>
          <h2 className="text-xl md:text-2xl font-black mt-1">{label} Reading — Full Practice Simulation</h2>
          <p className="text-sm text-fuchsia-100 mt-1 max-w-lg">
            3 sections · 40 questions · 60 minutes. Get an instant Winhouse
            Estimated IELTS Reading Practice Band with detailed analytics
            and post-test review.
          </p>
        </div>
        <button onClick={onStart}
          data-testid="ielts-reading-sim-start-cta"
          className="px-5 py-2.5 rounded-full bg-white text-indigo-700 hover:bg-indigo-50 font-black text-sm inline-flex items-center gap-2 transition">
          <PlayCircle className="h-4 w-4" /> Start Reading Simulation
        </button>
      </div>
    </div>
  );
}

function SimulationBanner({ history, onStart, onOpenResult }) {
  const inProgress = (history || []).find((h) => h.status === 'in_progress');
  const completed = (history || []).filter((h) => h.status === 'completed').slice(0, 3);
  return (
    <div className="mt-4 rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-500 to-purple-500 text-white p-5 md:p-6 shadow-lg"
       data-testid="ielts-sim-banner">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[11px] uppercase tracking-widest text-indigo-100 font-bold inline-flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5" /> Full Simulation
          </div>
          <h2 className="text-xl md:text-2xl font-black mt-1">IELTS Listening — Full Practice Simulation</h2>
          <p className="text-sm text-indigo-100 mt-1 max-w-lg">
            4 Parts · 40 questions · ~30 minutes. Get an instant Winhouse Estimated
            IELTS Practice Band with detailed analytics and post-test review.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <button onClick={onStart}
            data-testid="ielts-sim-start-cta"
            className="px-5 py-2.5 rounded-full bg-white text-indigo-700 hover:bg-indigo-50 font-black text-sm inline-flex items-center gap-2 transition">
            <PlayCircle className="h-4 w-4" />
            {inProgress ? 'Resume Simulation' : 'Start Full Simulation'}
          </button>
          {inProgress && (
            <div className="text-[11px] text-indigo-100 text-right">In-progress attempt found — click to resume</div>
          )}
        </div>
      </div>
      {completed.length > 0 && (
        <div className="mt-4 border-t border-white/20 pt-3">
          <div className="text-[10px] uppercase tracking-widest text-indigo-100 font-bold flex items-center gap-1.5">
            <History className="h-3 w-3" /> Recent attempts
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="ielts-sim-history">
            {completed.map((h) => (
              <button key={h.id} onClick={() => onOpenResult(h.id)}
                data-testid={`ielts-sim-history-${h.id}`}
                className="text-left rounded-lg bg-indigo-800/40 hover:bg-indigo-800/60 border border-indigo-400/30 p-2.5 text-xs">
                <div className="font-bold text-white tabular-nums">
                  Band {(h.estimated_band ?? 0).toFixed(1)} · {h.raw_score ?? 0}/40
                </div>
                <div className="text-[11px] text-indigo-100">
                  {new Date(h.completed_at || h.started_at).toLocaleDateString()} · View results →
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PracticeView({ kind, set, onSubmitResult, onBack }) {
  const [responses, setResponses] = useState({});
  const [busy, setBusy] = useState(false);
  const done = useMemo(() => Object.keys(responses).length === (set.questions?.length || 0), [responses, set]);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await ax.post(`/api/ielts/v2/${kind}-sets/${set.id}/submit`, { responses, mode: 'practice' });
      onSubmitResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Submit failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 md:p-6 space-y-4" data-testid={`ielts-practice-${kind}`}>
      <div className="flex items-baseline gap-2">
        <button onClick={onBack} className="text-xs text-slate-500 hover:text-slate-800">← Back</button>
        <h2 className="text-xl font-bold text-[#172033]">{set.title}</h2>
      </div>
      {kind === 'listening' ? (
        <audio controls preload="metadata" className="w-full" data-testid="ielts-listening-audio">
          <source src={`${BACKEND}${set.audio_url}`} />
        </audio>
      ) : (
        <div className="rounded-lg bg-slate-50 border p-4 whitespace-pre-wrap text-sm" data-testid="ielts-reading-passage">
          {set.passage}
        </div>
      )}
      {set.instructions && <div className="text-xs uppercase tracking-widest text-amber-600 font-bold">{set.instructions}</div>}
      <div className="space-y-3">
        {(set.questions || []).map((q) => (
          <div key={q.id} className="border rounded-lg p-3" data-testid={`ielts-q-${q.id}`}>
            <div className="text-sm font-semibold text-[#172033]">{q.number}. {q.prompt}</div>
            {q.options ? (
              <div className="mt-2 space-y-1">
                {q.options.map((opt) => {
                  const letter = opt.split('.')[0].trim();
                  return (
                    <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="radio" name={q.id} value={letter}
                        checked={responses[q.id] === letter}
                        onChange={() => setResponses((m) => ({ ...m, [q.id]: letter }))}
                        data-testid={`ielts-q-${q.id}-opt-${letter}`} />
                      {opt}
                    </label>
                  );
                })}
              </div>
            ) : (
              <input type="text" className="mt-2 w-full border rounded px-2 py-1.5 text-sm"
                placeholder={q.word_limit ? `Max ${q.word_limit} words` : 'Your answer'}
                value={responses[q.id] || ''}
                onChange={(e) => setResponses((m) => ({ ...m, [q.id]: e.target.value }))}
                data-testid={`ielts-q-${q.id}-input`} />
            )}
          </div>
        ))}
      </div>
      <button onClick={submit} disabled={busy || !done}
        data-testid="ielts-submit"
        className="px-5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold disabled:opacity-40 inline-flex items-center gap-1.5">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit for marking <ArrowRight className="h-4 w-4" />
      </button>
      {!done && <div className="text-[11px] text-slate-400">Answer every question to submit.</div>}
    </div>
  );
}

function ResultsView({ set, kind, attempt, onRetry, onBack }) {
  const s = attempt.score || {};
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 md:p-6 space-y-4" data-testid="ielts-results">
      <div className="flex flex-wrap items-baseline gap-3">
        <div className="text-3xl font-black text-emerald-600" data-testid="ielts-score">{s.correct}/{s.total}</div>
        <div className="text-sm text-slate-500">{s.percentage}% accuracy · automatically marked</div>
        <button onClick={onBack} className="ml-auto text-xs text-slate-500 hover:text-slate-800">← Choose another set</button>
        <button onClick={onRetry} className="text-xs px-3 py-1.5 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white font-bold">Retry (new attempt)</button>
      </div>
      {attempt.transcript && (
        <details className="rounded-lg bg-slate-50 border p-3" data-testid="ielts-transcript">
          <summary className="text-sm font-semibold text-[#172033] cursor-pointer">Show transcript</summary>
          <div className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{attempt.transcript}</div>
        </details>
      )}
      <div className="space-y-3">
        {(attempt.results || []).map((r, i) => (
          <div key={r.question_id} className={`border rounded-lg p-3 ${r.correct ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}
               data-testid={`ielts-result-${r.question_id}`}>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#172033]">
              {r.correct ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-rose-600" />}
              Q{i + 1} · {r.question_type}
            </div>
            <div className="text-sm mt-1"><b>Your answer:</b> {String(r.your_answer ?? '—')}</div>
            <div className="text-sm"><b>Correct:</b> {r.correct_answer}</div>
            {r.word_limit_note && <div className="text-xs text-amber-700 mt-1">{r.word_limit_note}</div>}
            {r.explanation && <div className="text-xs text-slate-600 mt-1"><b>Why:</b> {r.explanation}</div>}
            {r.listening_skill && <div className="text-xs text-slate-500 mt-1"><b>Skill:</b> {r.listening_skill}</div>}
            {r.strategy && <div className="text-xs text-slate-500"><b>Strategy:</b> {r.strategy}</div>}
            {r.transcript_evidence && <div className="text-xs text-slate-500 italic mt-1">Transcript: "{r.transcript_evidence}"</div>}
            {r.passage_evidence && <div className="text-xs text-slate-500 italic mt-1">Passage: "{r.passage_evidence}"</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
